import { IKMeansParameters } from '../k-means-interfaces';
import {
    euclideanDistance,
    euclideanDistanceExplanation, getPointKey,
    manhattanDistance,
    manhattanDistanceExplanation, round
} from '../../cluster-provider';
import { DistanceType } from '../../cluster-interfaces';

export const getMapKey = (centroid: number[], point: number[]): string => {
    return `${ getPointKey(centroid) }_${ getPointKey(point) }`;
};

export const getAverage = (points: number[][]): number[] => {
    if (points.length === 0) {
        throw new Error("Points array must not be empty.");
    }

    const dimension = points[0].length;
    const sums = new Array(dimension).fill(0);

    for (const point of points) {
        if (point.length !== dimension) {
            throw new Error('All points must have the same number of dimensions.');
        }
        for (let i = 0; i < dimension; i++) {
            sums[i] += point[i];
        }
    }

    return sums.map(sum => round(Number((sum / points.length))));
};

export const getAverageExplanation = (points: number[][]): string => {
    if (points.length === 0) {
        throw new Error("Points array must not be empty.");
    }

    const dimension = points[0].length;
    const sums = new Array(dimension).fill(0);
    const parts: string[] = [];

    for (const point of points) {
        if (point.length !== dimension) {
            throw new Error('All points must have the same number of dimensions.');
        }
        for (let i = 0; i < dimension; i++) {
            sums[i] += point[i];
        }
    }

    for (let i = 0; i < dimension; i++) {
        const sumParts = points.map(p => p[i]).join(" + ");
        const sumValue = sums[i];
        const average = round(Number((sumValue / points.length)));
        parts.push(`(${sumParts})/${points.length} = ${average}`);
    }

    return parts.join(" | ");
};

const areCentroidsTheSame = (centroidsGroup1: number[][], centroidsGroup2: number[][]): boolean => {
    if (centroidsGroup1.length !== centroidsGroup2.length) {
        return false;
    }

    for (let i = 0; i < centroidsGroup1.length; i++) {
        const c1 = centroidsGroup1[i];
        const c2 = centroidsGroup2[i];

        if (c1.length !== c2.length) {
            return false;
        }

        for (let j = 0; j < c1.length; j++) {
            if (c1[j] !== c2[j]) {
                return false;
            }
        }
    }

    return true;
};

export const getPointIndex = (points: number[][], point: number[]): number => {
    for (let i = 0; i < points.length; i++) {
        const current = points[i];
        if (current.length !== point.length) {
            continue;
        }

        let isEqual = true;
        for (let j = 0; j < point.length; j++) {
            if (current[j] !== point[j]) {
                isEqual = false;
                break;
            }
        }

        if (isEqual) {
            return i;
        }
    }

    return -1;
};

export const calculateKMeansIteration = (params: IKMeansParameters, distance: DistanceType): {
    tableMap: Map<string, number>;
    calculations: string[];
    groups: Map<string, number[][]>;
    newCenters: number[][];
    newCentersCalculations: string[],
    oldAndNewCentroidsTheSame: boolean
} => {

    // centroid_point ---> distance
    const tableMap: Map<string, number> = new Map();
    const calculations: string[] = [];

    for(let c= 0; c<params.centroids.length; c++) {
        const centroid = params.centroids[c];
        const centroidPrefix = `${ params.centroidsPrefix }${ c + 1 }`;

        for(let p=0; p<params.points.length; p++) {
            const point = params.points[p];
            const pointPrefix = `${ params.pointsPrefix }${ p + 1 }`;
            const calcPrefix = `d(${ centroidPrefix }, ${ pointPrefix }) = `

            let d = 0;
            switch (distance) {
                case DistanceType.Manhattan: {
                    d = manhattanDistance(centroid, point);

                    const calc = manhattanDistanceExplanation(centroid, point);
                    calculations.push(calcPrefix + calc);
                    break;
                }

                case DistanceType.Euclidean: {
                    d = euclideanDistance(centroid, point);

                    const calc = euclideanDistanceExplanation(centroid, point);
                    calculations.push(calcPrefix + calc);
                    break;
                }
            }

            const key = getMapKey(centroid, point);
            tableMap.set(key, d);
        }
    }

    const groups: Map<string, number[][]> = new Map();

    for(let p= 0; p<params.points.length; p++) {
        const point = params.points[p];

        let minDistance = Infinity;
        let minCentroid: number[]|null = null;

        for(let c= 0; c<params.centroids.length; c++) {
            const centroid = params.centroids[c];
            const key = getMapKey(centroid, point);
            const d = tableMap.get(key);
            if(d < minDistance) {
                minDistance = d;
                minCentroid = centroid;
            }
        }

        if(!minCentroid) {
            throw new Error('Mo min centroid.');
        }

        const key = getPointKey(minCentroid);
        const group = groups.get(key) || [];
        group.push(point);
        groups.set(key, group);
    }

    const newCenters: number[][] = [];
    const newCentersCalculations: string[] = [];
    for(const [_centroidKey, group] of groups) {
        const newCenter = getAverage(group);
        newCenters.push(newCenter);
        newCentersCalculations.push(getAverageExplanation(group));
    }

    const oldAndNewCentroidsTheSame = areCentroidsTheSame(params.centroids, newCenters);

    return {
        tableMap,
        calculations,
        groups,
        newCenters,
        newCentersCalculations,
        oldAndNewCentroidsTheSame,
    };
};